import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Bot, FileText, Loader2, MessageSquarePlus, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { FilePreviewDialog } from "@/components/FilePreviewDialog";

const inline = (s) => s.split(/(\*\*[^*]+\*\*)/g).map((p, i) => (p.startsWith("**") && p.endsWith("**") ? <strong key={i}>{p.slice(2, -2)}</strong> : p));

const Markdown = ({ text }) => (
  <div className="space-y-1.5">
    {text.split("\n").map((line, i) => {
      const t = line.trim();
      if (!t) return null;
      const h = t.match(/^#{1,4}\s+(.*)/);
      if (h) return <p key={i} className="font-semibold text-slate-900 pt-1">{inline(h[1])}</p>;
      const b = t.match(/^[*-]\s+(.*)/) || t.match(/^(\d+\.\s+.*)/);
      if (b) return <p key={i} className="pl-4 relative before:content-['•'] before:absolute before:left-1 before:text-slate-400">{inline(b[1])}</p>;
      return <p key={i}>{inline(t)}</p>;
    })}
  </div>
);

const Bubble = ({ m, onSource }) => (
  <div className="space-y-2">
    <div className="flex justify-end"><div className="max-w-[80%] rounded-2xl rounded-tr-sm bg-slate-900 text-white px-4 py-2 text-sm whitespace-pre-line">{m.message}</div></div>
    <div className="flex gap-2">
      <div className="w-7 h-7 rounded-full bg-purple-100 flex items-center justify-center shrink-0"><Bot className="w-4 h-4 text-purple-700" /></div>
      <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-white border px-4 py-3 text-sm leading-relaxed">
        {m.response ? <Markdown text={m.response} /> : <span className="text-slate-400 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" />Analyse de ton dossier…</span>}
        {m.sources?.length > 0 && (
          <div className="mt-3 pt-2 border-t flex flex-wrap gap-1.5">
            <span className="text-[11px] text-slate-500 w-full">Sources (coffre-fort) :</span>
            {m.sources.map((s) => (
              <button key={s.ref} onClick={() => onSource(s)} className="text-[11px] px-2 py-1 rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 inline-flex items-center gap-1" data-testid={`chat-source-${s.ref}`}>
                <FileText className="w-3 h-3" />[{s.ref}] {s.filename}</button>
            ))}
          </div>
        )}
      </div>
    </div>
  </div>
);

const Sessions = ({ sessions, current, onPick, onNew, onDelete }) => (
  <div className="w-full md:w-64 shrink-0 space-y-2" data-testid="chat-sessions">
    <Button className="w-full" variant="outline" onClick={onNew} data-testid="chat-new-btn"><MessageSquarePlus className="w-4 h-4 mr-1.5" />Nouvelle conversation</Button>
    <div className="space-y-1 max-h-[480px] overflow-y-auto">
      {sessions.map((s) => (
        <div key={s.id} className={`group flex items-center gap-1 rounded-md px-2 py-1.5 text-sm cursor-pointer ${current === s.id ? "bg-slate-900 text-white" : "hover:bg-slate-100"}`}
          onClick={() => onPick(s.id)} data-testid={`chat-session-${s.id}`}>
          <span className="flex-1 truncate">{s.title}</span>
          <button onClick={(e) => { e.stopPropagation(); onDelete(s.id); }} className="opacity-0 group-hover:opacity-100" data-testid={`chat-session-del-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      ))}
      {sessions.length === 0 && <p className="text-xs text-slate-500 px-2">Aucune conversation enregistrée.</p>}
    </div>
  </div>
);

export const LegalAiChat = ({ seed, onSeedConsumed }) => {
  const [sessions, setSessions] = useState([]);
  const [current, setCurrent] = useState(null);
  const [messages, setMessages] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const endRef = useRef(null);

  const loadSessions = () => api.get("/legal/ai-chat/sessions").then((r) => setSessions(r.data.sessions)).catch(() => {});
  useEffect(() => {
    loadSessions();
    api.get("/legal/chatbot/suggestions/dynamic").then((r) => setSuggestions(r.data.suggestions || [])).catch(() => {});
  }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);
  const seedDone = useRef(null);
  useEffect(() => {
    if (!seed?.message || seedDone.current === seed.nonce) { return; }
    seedDone.current = seed.nonce;
    setCurrent(null); setMessages([]); send(seed.message);
    onSeedConsumed?.();
  }, [seed]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = async (sid) => {
    setCurrent(sid);
    try { const r = await api.get(`/legal/ai-chat/sessions/${sid}`); setMessages(r.data.messages); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async (sid) => {
    if (!window.confirm("Supprimer cette conversation ?")) return;
    await api.delete(`/legal/ai-chat/sessions/${sid}`).catch((e) => toast.error(apiError(e)));
    if (sid === current) { setCurrent(null); setMessages([]); }
    loadSessions();
  };
  const send = async (text) => {
    const msg = (text || input).trim();
    if (msg.length < 2 || loading) return;
    setInput(""); setLoading(true);
    setMessages((m) => [...m, { id: "pending", message: msg }]);
    try {
      const r = await api.post("/legal/ai-chat/ask", { message: msg, session_id: current }, { timeout: 180000 });
      setMessages((m) => [...m.filter((x) => x.id !== "pending"), { id: Date.now(), message: msg, response: r.data.response, sources: r.data.sources }]);
      if (!current) { setCurrent(r.data.session_id); loadSessions(); }
    } catch (e) {
      toast.error(apiError(e));
      setMessages((m) => m.filter((x) => x.id !== "pending"));
      setInput(msg);
    } finally { setLoading(false); }
  };

  return (
    <Card data-testid="legal-ai-chat">
      <FilePreviewDialog file={preview} onClose={() => setPreview(null)} />
      <CardContent className="p-4 flex flex-col md:flex-row gap-4">
        <Sessions sessions={sessions} current={current} onPick={pick} onDelete={del} onNew={() => { setCurrent(null); setMessages([]); }} />
        <div className="flex-1 flex flex-col min-h-[480px]">
          <div className="flex-1 overflow-y-auto space-y-4 bg-slate-50 rounded-lg p-4 max-h-[520px]">
            {messages.length === 0 && (
              <div className="space-y-2">
                <p className="text-sm text-slate-600">Je connais ton profil, tes dirigeants, associés, registres, échéances et documents du coffre-fort. Questions suggérées selon ton dossier :</p>
                {suggestions.map((s, i) => (
                  <button key={i} onClick={() => send(s)} className="block text-left w-full p-2.5 bg-white border hover:border-purple-300 rounded-md text-sm transition-colors" data-testid={`chat-sugg-${i}`}>{s}</button>
                ))}
              </div>
            )}
            {messages.map((m) => <Bubble key={m.id} m={m} onSource={(s) => setPreview({ src: `/legal/documents/${s.document_id}/download`, name: s.filename })} />)}
            <div ref={endRef} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2 mt-3">
            <Textarea rows={2} placeholder="Pose ta question juridique… (Entrée pour envoyer)" value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} disabled={loading} data-testid="ai-chat-input" />
            <Button type="submit" disabled={loading || input.trim().length < 2} className="self-end" data-testid="ai-chat-send">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</Button>
          </form>
          <p className="text-[10px] text-slate-400 mt-1">Gemini 2.5 Pro · réponses indicatives, à faire valider par un avocat ou expert-comptable pour les opérations importantes.</p>
        </div>
      </CardContent>
    </Card>
  );
};
